import React, { useEffect, useMemo, useRef, useState } from "react";

import StudyGateBadge from "../StudyGateBadge/StudyGateBadge";
import {
  fetchGateAdvanceOverview,
  fetchStudyGateAdvanceReadiness,
  gateNameForCode,
  getNextGateOption,
  isGateAdvanceAllowed,
  tryAdvanceStudyGate,
} from "../../Pages/isf/isfStudyGateService";
import "../../Pages/isf/IsfSiteOverviewPanel.scss";
import "./StudyGateAdvanceModal.scss";

const EMPTY_ROLE_OPTIONS = Object.freeze([]);

function KpiTile({ label, value, sub, tone = "default" }) {
  return (
    <div className={`isf-site-overview__kpi isf-site-overview__kpi--${tone}`}>
      <div className="isf-site-overview__kpi-value">{value}</div>
      <div className="isf-site-overview__kpi-label">{label}</div>
      {sub ? <div className="isf-site-overview__kpi-sub">{sub}</div> : null}
    </div>
  );
}

function ReadonlyTaskRow({ task }) {
  const statusClass =
    task.status === "Overdue"
      ? "overdue"
      : task.type === "sign"
        ? "sign"
        : "pending";

  return (
    <div className="isf-site-overview__task">
      <div className="isf-site-overview__task-icon" aria-hidden="true">
        {task.type === "sign" ? (
          <i className="fas fa-file-signature" />
        ) : (
          <i className="fas fa-upload" />
        )}
      </div>
      <div className="isf-site-overview__task-body">
        <div className="isf-site-overview__task-title" title={task.title}>
          {task.title}
        </div>
        <div className="isf-site-overview__task-sub" title={task.subtitle}>
          {task.subtitle}
        </div>
      </div>
      <span
        className={`isf-site-overview__task-status isf-site-overview__task-status--${statusClass}`}
      >
        {task.status}
      </span>
    </div>
  );
}

const StudyGateAdvanceModal = ({
  open,
  onClose,
  studyId,
  studyLabel = "",
  currentGateCode,
  userData,
  siteIdRef = null,
  siteIds = null,
  templateCategory = "",
  roleOptions,
  onAdvanced,
}) => {
  const effectiveRoleOptions = roleOptions ?? EMPTY_ROLE_OPTIONS;
  const [loading, setLoading] = useState(false);
  const [advancing, setAdvancing] = useState(false);
  const [summary, setSummary] = useState(null);
  const [sites, setSites] = useState([]);
  const [overview, setOverview] = useState({ kpis: {}, tasks: [] });
  const [error, setError] = useState("");
  const loadSeqRef = useRef(0);

  const roleOptionsKey = useMemo(
    () =>
      effectiveRoleOptions
        .map((role) => String(role?.value ?? role?.id ?? role?.RoleId ?? ""))
        .filter(Boolean)
        .join("|"),
    [effectiveRoleOptions],
  );

  const siteScopeKey = useMemo(() => {
    if (siteIdRef) return `site:${siteIdRef}`;
    const ids = (siteIds || [])
      .map((site) => String(site?.siteId ?? site?.SiteId ?? site?.value ?? site ?? "").trim())
      .filter(Boolean)
      .sort()
      .join("|");
    return ids ? `sites:${ids}` : "study-wide";
  }, [siteIdRef, siteIds]);

  useEffect(() => {
    if (!open || !studyId || !userData?.OrganizationId) return undefined;

    const seq = ++loadSeqRef.current;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      setOverview({ kpis: {}, tasks: [] });
      try {
        const result = await fetchStudyGateAdvanceReadiness({ userData, studyId });
        if (cancelled || seq !== loadSeqRef.current) return;

        const nextSummary = result.summary;
        setSummary(nextSummary);
        setSites(result.sites || []);

        const gateCode =
          nextSummary?.CurrentGateCode ||
          nextSummary?.currentGateCode ||
          currentGateCode ||
          "PRE_STUDY";

        const snapshot = await fetchGateAdvanceOverview({
          userData,
          studyId,
          gateCode,
          siteIdRef,
          siteIds,
          templateCategory,
          roleOptions: effectiveRoleOptions,
        });
        if (cancelled || seq !== loadSeqRef.current) return;
        setOverview(snapshot);
      } catch (err) {
        if (cancelled || seq !== loadSeqRef.current) return;
        setError(err?.message || "Failed to validate gate readiness.");
        setSummary(null);
        setSites([]);
        setOverview({ kpis: {}, tasks: [] });
      } finally {
        if (!cancelled && seq === loadSeqRef.current) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [
    open,
    studyId,
    userData?.OrganizationId,
    userData?.StaffId,
    currentGateCode,
    siteIdRef,
    siteScopeKey,
    templateCategory,
    roleOptionsKey,
  ]);

  const openItems = overview.tasks || [];
  const kpis = overview.kpis || {};
  const uploadLabel =
    kpis.uploadTotal > 0 ? `${kpis.uploadDone}/${kpis.uploadTotal}` : "—";
  const signLabel =
    kpis.signTotal > 0 ? `${kpis.signDone}/${kpis.signTotal}` : "—";

  const pendingSiteCount = useMemo(
    () =>
      sites.filter((site) => {
        const pending = Number(site.PendingCount ?? site.pendingCount) || 0;
        const ready =
          site.IsReady === 1 ||
          site.IsReady === true ||
          site.isReady === 1 ||
          pending === 0;
        return !ready;
      }).length,
    [sites],
  );

  if (!open) return null;

  const canAdvance = isGateAdvanceAllowed(summary);
  const currentCode =
    summary?.CurrentGateCode || summary?.currentGateCode || currentGateCode;
  const currentName = gateNameForCode(currentCode);
  const nextCode = summary?.NextGateCode || summary?.nextGateCode || "";
  const nextName =
    summary?.NextGateName ||
    summary?.nextGateName ||
    (nextCode ? gateNameForCode(nextCode) : "");
  const fallbackNext = getNextGateOption([], currentCode);
  const advanceTargetName = nextName || fallbackNext?.name || "next phase";
  const message = summary?.MSG || summary?.msg || summary?.Message || "";

  const handleAdvance = async () => {
    if (!canAdvance || advancing) return;
    setAdvancing(true);
    setError("");
    try {
      const { result } = await tryAdvanceStudyGate({ userData, studyId });
      const advanced =
        result?.Advanced === 1 ||
        result?.Advanced === true ||
        result?.advanced === 1;
      if (!advanced) {
        setError(
          result?.MSG ||
            result?.msg ||
            message ||
            "Phase could not be advanced. Complete pending eISF items at all sites.",
        );
        return;
      }
      onAdvanced?.(result);
      onClose?.();
    } catch (err) {
      setError(err?.message || "Failed to advance study phase.");
    } finally {
      setAdvancing(false);
    }
  };

  return (
    <div
      className="modal fade show d-block study-gate-advance-modal"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Advance study phase</h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
            />
          </div>
          <div className="modal-body">
            {studyLabel ? (
              <p className="small text-secondary mb-2">{studyLabel}</p>
            ) : null}

            <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
              <StudyGateBadge code={currentCode} label={currentName} compact />
              {nextName || fallbackNext?.name ? (
                <>
                  <i className="fas fa-arrow-right text-muted" aria-hidden="true" />
                  <StudyGateBadge
                    code={nextCode || fallbackNext?.code || ""}
                    label={nextName || fallbackNext?.name || ""}
                    compact
                  />
                </>
              ) : null}
            </div>

            <p className="study-gate-advance-modal__intro small text-secondary mb-3">
              Study phases advance <strong>one step at a time</strong> only — you
              cannot skip ahead (for example, Pre-study to Conduct) or move
              backwards. Complete the open items below for{" "}
              <strong>{currentName}</strong> at every site before advancing to{" "}
              {nextName || fallbackNext?.name ? (
                <strong>{nextName || fallbackNext?.name}</strong>
              ) : (
                "the next phase"
              )}.
            </p>

            {loading ? (
              <p className="small text-secondary">
                Checking eISF completion for {currentName}…
              </p>
            ) : null}

            {!loading && message ? (
              <p className={`small ${canAdvance ? "text-success" : "text-danger"}`}>
                {message}
              </p>
            ) : null}

            {!loading ? (
              <div className="study-gate-advance-modal__overview isf-site-overview">
                <div className="isf-site-overview__kpis">
                  <KpiTile
                    label="Uploads"
                    value={uploadLabel}
                    sub="Documents uploaded"
                    tone="upload"
                  />
                  <KpiTile
                    label="Signatures"
                    value={signLabel}
                    sub="Signer completions"
                    tone="sign"
                  />
                  <KpiTile
                    label="Overdue"
                    value={kpis.overdueCount || 0}
                    sub="Need attention"
                    tone={kpis.overdueCount > 0 ? "warn" : "default"}
                  />
                  <KpiTile
                    label="Sites pending"
                    value={pendingSiteCount || 0}
                    sub="Not ready to advance"
                    tone={pendingSiteCount > 0 ? "warn" : "default"}
                  />
                </div>

                <div className="isf-site-overview__section isf-site-overview__section--list">
                  <div className="isf-site-overview__section-head">
                    <h6>Pending before phase change</h6>
                    <span>{openItems.length} open</span>
                  </div>
                  {openItems.length ? (
                    <div className="isf-site-overview__task-list isf-site-overview__task-list--scroll isf-scroll-light study-gate-advance-modal__task-list">
                      {openItems.map((task) => (
                        <ReadonlyTaskRow key={task.id} task={task} />
                      ))}
                    </div>
                  ) : (
                    <div className="isf-site-overview__empty">
                      <p>
                        No open uploads or signatures found for {currentName}.
                        {canAdvance
                          ? " You can advance to the next phase."
                          : " Check that all sites are mapped and placeholders are applied."}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : null}

            {error ? <p className="small text-danger mt-2 mb-0">{error}</p> : null}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
              Close
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleAdvance}
              disabled={!canAdvance || loading || advancing || !(nextCode || fallbackNext?.code)}
            >
              {advancing
                ? "Advancing…"
                : `Advance to ${advanceTargetName}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudyGateAdvanceModal;
